/**
 * Live run modal (§8.2, interlocks §10.4–10.5): shows the server-side
 * interlock checklist for the selected dry-run preview, pins the batch
 * label, and requires typing it exactly to arm the launch button.
 * Rehearsal mode (D-33) targets the spreadsheet copies from config.
 */

import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Code,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Switch,
  Text,
} from '@chakra-ui/react';
import { payrollApi } from '../api';

export default function LiveRunModal({ isOpen, onClose, previewRun, onLaunched }) {
  const [rehearsal, setRehearsal] = useState(false);
  const [report, setReport] = useState(null);
  const [reportError, setReportError] = useState(null);
  const [confirmText, setConfirmText] = useState('');
  const [launching, setLaunching] = useState(false);
  const [launchError, setLaunchError] = useState(null);

  const loadReport = useCallback(async (asRehearsal) => {
    setReport(null);
    setReportError(null);
    setConfirmText('');
    try {
      const res = await payrollApi.livePreflight({ previewRunId: previewRun.id, rehearsal: asRehearsal });
      setReport(res);
    } catch (err) {
      setReportError(err.message);
    }
  }, [previewRun]);

  useEffect(() => {
    if (isOpen) {
      setRehearsal(false);
      setLaunchError(null);
      loadReport(false);
    }
  }, [isOpen, loadReport]);

  const toggleRehearsal = (on) => {
    setRehearsal(on);
    loadReport(on);
  };

  const armed = !!(report && report.ok && confirmText === report.batchLabel);

  const launch = async () => {
    setLaunching(true);
    setLaunchError(null);
    try {
      const res = await payrollApi.liveRun({
        previewRunId: previewRun.id,
        batchLabel: report.batchLabel,
        confirmText,
        rehearsal,
      });
      onLaunched(res.runId, rehearsal);
      onClose();
    } catch (err) {
      setLaunchError(err.message);
    } finally {
      setLaunching(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={launching ? () => {} : onClose} size="lg" closeOnOverlayClick={!launching}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {rehearsal ? 'Rehearsal live run (spreadsheet copies)' : 'Live run'}
          {rehearsal && <Badge ml={2} colorScheme="blue">D-33 rehearsal</Badge>}
        </ModalHeader>
        <ModalCloseButton isDisabled={launching} />
        <ModalBody>
          <Flex align="center" gap={2} mb={3}>
            <Switch size="sm" colorScheme="blue" isChecked={rehearsal}
              onChange={(e) => toggleRehearsal(e.target.checked)} isDisabled={launching} />
            <Text fontSize="sm">
              Rehearsal mode — write to the configured spreadsheet <b>copies</b> instead of the real
              pipeline/workbook (real sheets untouched; live_enabled not required).
            </Text>
          </Flex>

          {!report && !reportError && <Flex justify="center" py={6}><Spinner /></Flex>}
          {reportError && (
            <Alert status="error" borderRadius="md" fontSize="sm">
              <AlertIcon />{reportError}
              <Button ml={2} size="xs" onClick={() => loadReport(rehearsal)}>Retry</Button>
            </Alert>
          )}

          {report && (
            <>
              <Box borderWidth="1px" borderRadius="md" p={3} mb={3}>
                {report.checks.map((c) => (
                  <Flex key={c.id} align="baseline" gap={2} py={0.5}>
                    <Badge colorScheme={c.ok ? 'green' : 'red'} minW="46px" textAlign="center">
                      {c.ok ? 'PASS' : 'FAIL'}
                    </Badge>
                    <Box>
                      <Text fontSize="sm">{c.label}</Text>
                      {c.detail && <Text fontSize="xs" color="gray.500">{c.detail}</Text>}
                    </Box>
                  </Flex>
                ))}
              </Box>

              {!report.ok && (
                <Alert status="warning" borderRadius="md" mb={3} fontSize="sm">
                  <AlertIcon />Fix the failing interlocks, then reopen this modal.
                </Alert>
              )}

              {report.ok && (
                <>
                  <Alert status={rehearsal ? 'info' : 'error'} borderRadius="md" mb={3} fontSize="sm">
                    <AlertIcon />
                    {rehearsal
                      ? 'Rehearsal will stamp and write the spreadsheet COPIES exactly like a real run.'
                      : 'This writes commissions, formulas, flags, and batch stamps to the live pipeline and pastes payee rows into the Bank & LO Workbook. Run only while the sheet is quiet.'}
                  </Alert>
                  <FormControl>
                    <FormLabel fontSize="sm">
                      Type the batch label <Code>{report.batchLabel}</Code> to confirm
                    </FormLabel>
                    <Input
                      size="sm"
                      fontFamily="mono"
                      value={confirmText}
                      onChange={(e) => setConfirmText(e.target.value)}
                      placeholder="MM/dd HH:mm"
                      autoFocus
                    />
                  </FormControl>
                </>
              )}
            </>
          )}

          {launchError && (
            <Alert status="error" borderRadius="md" mt={3} fontSize="sm">
              <AlertIcon />{launchError}
            </Alert>
          )}
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={2} onClick={onClose} isDisabled={launching}>Cancel</Button>
          <Button
            colorScheme={rehearsal ? 'blue' : 'red'}
            onClick={launch}
            isDisabled={!armed}
            isLoading={launching}
            loadingText="Running…"
          >
            {rehearsal ? 'Run rehearsal' : 'Run LIVE payroll'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
